import React, { useState, useEffect } from 'react';
import { 
  Loader2, Building, Mail, Edit, Save, X, Phone, Globe, MapPin,
  Facebook, Twitter, Linkedin, Instagram, Youtube, Users, Percent, School,Plus, GraduationCap, BookOpen
} from 'lucide-react';
import ImageUpload from '../components/ImageUpload';

const CollegeProfile = () => {
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('basic');
  const [college, setCollege] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    contactEmail: '',
    logo: '',
    about: '',
    degrees: [],
    address: {
      street: '',
      city: '',
      state: '',
      country: '',
      postalCode: '',
    },
    contact: {
      phone: '',
      alternateEmail: '',
      fax: '',
      website: ''
    },
    socialLinks: {
      facebook: '',
      twitter: '',
      linkedin: '',
      instagram: '',
      youtube: ''
    },
    departments: [],
    stats: {
      totalStudents: '',
      totalFaculty: '',
      totalAlumni: '',
      placementRate: ''
    }
  });

  useEffect(() => {
    fetchCollegeProfile();
  }, []);

  const fetchCollegeProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/college/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch profile');
      const data = await response.json();
      setCollege(data);
      setFormData({
        ...formData,
        ...data,
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      console.log("Submitting form data:", formData); // Add this to debug
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/college/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
  
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update profile');
      }
      
      const updatedData = await response.json();
      setCollege(updatedData);
      setEditing(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const addDepartment = () => {
    setFormData({
      ...formData,
      departments: [...formData.departments, { name: '' }]
    });
  };

  const removeDepartment = (index) => {
    setFormData({
      ...formData,
      departments: formData.departments.filter((_, i) => i !== index)
    });
  };

  const updateDepartment = (index, value) => {
    const newDepartments = [...formData.departments];
    newDepartments[index] = { name: value };
    setFormData({
      ...formData,
      departments: newDepartments
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="col-span-full">
                <label className="block text-sm font-medium text-gray-700 mb-2">College Logo</label>
                <ImageUpload
                  onImageUpload={(url) => setFormData({ ...formData, logo: url })}
                  existingImage={formData.logo}
                  className="w-40 h-40"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">College Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">College Code</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Contact Email</label>
                <input
                  type="email"
                  value={formData.contactEmail}
                  onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Phone</label>
                <input
                  type="tel"
                  value={formData.contact?.phone || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    contact: { ...formData.contact, phone: e.target.value }
                  })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                />
              </div>

              <div className="col-span-full">
                <label className="block text-sm font-medium text-gray-700">About</label>
                <textarea
                  value={formData.about || ''}
                  onChange={(e) => setFormData({ ...formData, about: e.target.value })}
                  rows={4}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                />
              </div>
            </div>
          </div>
        );

      case 'address':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900">Address Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="col-span-full">
                <label className="block text-sm font-medium text-gray-700">Street Address</label>
                <input
                  type="text"
                  value={formData.address?.street || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    address: { ...formData.address, street: e.target.value }
                  })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">City</label>
                <input
                  type="text"
                  value={formData.address?.city || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    address: { ...formData.address, city: e.target.value }
                  })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">State</label>
                <input
                  type="text"
                  value={formData.address?.state || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    address: { ...formData.address, state: e.target.value }
                  })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Country</label>
                <input
                  type="text"
                  value={formData.address?.country || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    address: { ...formData.address, country: e.target.value }
                  })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Postal Code</label>
                <input
                  type="text"
                  value={formData.address?.postalCode || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    address: { ...formData.address, postalCode: e.target.value }
                  })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                />
              </div>

             
            </div>
          </div>
        );

      case 'social':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900">Social Links</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Facebook</label>
                <div className="mt-1 flex rounded-md shadow-sm">
                  <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50">
                    <Facebook className="h-5 w-5 text-gray-400" />
                  </span>
                  <input
                    type="url"
                    value={formData.socialLinks?.facebook || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      socialLinks: { ...formData.socialLinks, facebook: e.target.value }
                    })}
                    className="flex-1 block w-full rounded-none rounded-r-md border-gray-300"
                    placeholder="https://facebook.com/..."
                  />
                </div>
              </div>

              {/* Similar input fields for other social links */}
              {/* Twitter */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Twitter</label>
                <div className="mt-1 flex rounded-md shadow-sm">
                  <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50">
                    <Twitter className="h-5 w-5 text-gray-400" />
                  </span>
                  <input
                    type="url"
                    value={formData.socialLinks?.twitter || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      socialLinks: { ...formData.socialLinks, twitter: e.target.value }
                    })}
                    className="flex-1 block w-full rounded-none rounded-r-md border-gray-300"
                    placeholder="https://twitter.com/..."
                  />
                </div>
              </div>

              {/* LinkedIn */}
              <div>
                <label className="block text-sm font-medium text-gray-700">LinkedIn</label>
                <div className="mt-1 flex rounded-md shadow-sm">
                  <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50">
                    <Linkedin className="h-5 w-5 text-gray-400" />
                  </span>
                  <input
                    type="url"
                    value={formData.socialLinks?.linkedin || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      socialLinks: { ...formData.socialLinks, linkedin: e.target.value }
                    })}
                    className="flex-1 block w-full rounded-none rounded-r-md border-gray-300"
                    placeholder="https://linkedin.com/..."
                  />
                </div>
              </div>

              {/* Instagram */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Instagram</label>
                <div className="mt-1 flex rounded-md shadow-sm">
                  <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50">
                    <Instagram className="h-5 w-5 text-gray-400" />
                  </span>
                  <input
                    type="url"
                    value={formData.socialLinks?.instagram || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      socialLinks: { ...formData.socialLinks, instagram: e.target.value }
                    })}
                    className="flex-1 block w-full rounded-none rounded-r-md border-gray-300"
                    placeholder="https://instagram.com/..."
                  />
                </div>
              </div>

              {/* YouTube */}
              <div>
                <label className="block text-sm font-medium text-gray-700">YouTube</label>
                <div className="mt-1 flex rounded-md shadow-sm">
                  <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50">
                  <Youtube className="h-5 w-5 text-gray-400" />
                  </span>
                  <input
                    type="url"
                    value={formData.socialLinks?.youtube || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      socialLinks: { ...formData.socialLinks, youtube: e.target.value }
                    })}
                    className="flex-1 block w-full rounded-none rounded-r-md border-gray-300"
                    placeholder="https://youtube.com/..."
                  />
                </div>
              </div>
            </div>
          </div>
        );
        case 'degrees':
          return (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">Degrees & Departments</h3>
                <button
                  type="button"
                  onClick={() => setFormData({
                    ...formData,
                    degrees: [...formData.degrees, { name: '', departments: [] }]
                  })}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  <GraduationCap className="h-4 w-4 mr-2" />
                  Add Degree
                </button>
              </div>
        
              <div className="space-y-6">
                {formData.degrees?.map((degree, degreeIndex) => (
                  <div key={degreeIndex} className="p-4 bg-gray-50 rounded-lg space-y-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1 mr-4">
                        <label className="block text-sm font-medium text-gray-700">Degree Name</label>
                        <input
                          type="text"
                          value={degree.name}
                          onChange={(e) => {
                            const newDegrees = [...formData.degrees];
                            newDegrees[degreeIndex].name = e.target.value;
                            setFormData({ ...formData, degrees: newDegrees });
                          }}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                          placeholder="e.g., BE, BSc, MSc"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const newDegrees = formData.degrees.filter((_, i) => i !== degreeIndex);
                          setFormData({ ...formData, degrees: newDegrees });
                        }}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>
        
                    <div className="pl-4 border-l-2 border-gray-200">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="text-sm font-medium text-gray-700">Departments</h4>
                        <button
                          type="button"
                          onClick={() => {
                            const newDegrees = [...formData.degrees];
                            newDegrees[degreeIndex].departments.push({ name: '' });
                            setFormData({ ...formData, degrees: newDegrees });
                          }}
                          className="inline-flex items-center px-2 py-1 text-sm border border-transparent rounded text-blue-600 hover:text-blue-700"
                        >
                          <BookOpen className="h-4 w-4 mr-1" />
                          Add Department
                        </button>
                      </div>
        
                      <div className="space-y-2">
                        {degree.departments.map((dept, deptIndex) => (
                          <div key={deptIndex} className="flex items-center space-x-2">
                            <input
                              type="text"
                              value={dept.name}
                              onChange={(e) => {
                                const newDegrees = [...formData.degrees];
                                newDegrees[degreeIndex].departments[deptIndex].name = e.target.value;
                                setFormData({ ...formData, degrees: newDegrees });
                              }}
                              className="block w-full rounded-md border-gray-300 shadow-sm text-sm"
                              placeholder="Department name"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const newDegrees = [...formData.degrees];
                                newDegrees[degreeIndex].departments = newDegrees[degreeIndex].departments
                                  .filter((_, i) => i !== deptIndex);
                                setFormData({ ...formData, degrees: newDegrees });
                              }}
                              className="text-red-500 hover:text-red-700"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
      case 'departments':
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">Departments</h3>
              <button
                type="button"
                onClick={addDepartment}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Department
              </button>
            </div>

            <div className="space-y-4">
              {formData.departments?.map((dept, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-lg relative">
                  <button
                    type="button"
                    onClick={() => removeDepartment(index)}
                    className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                  >
                    <X className="h-5 w-5" />
                  </button>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Department Name</label>
                    <input
                      type="text"
                      value={dept.name}
                      onChange={(e) => updateDepartment(index, e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'stats':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900">College Statistics</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Total Students</label>
                <input
                  type="number"
                  value={formData.stats?.totalStudents || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    stats: { ...formData.stats, totalStudents: parseInt(e.target.value) }
                  })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Total Faculty</label>
                <input
                  type="number"
                  value={formData.stats?.totalFaculty || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    stats: { ...formData.stats, totalFaculty: parseInt(e.target.value) }
                  })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Total Alumni</label>
                <input
                  type="number"
                  value={formData.stats?.totalAlumni || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    stats: { ...formData.stats, totalAlumni: parseInt(e.target.value) }
                  })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Placement Rate (%)</label>
                <input
                  type="number"
                  value={formData.stats?.placementRate || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    stats: { ...formData.stats, placementRate: parseInt(e.target.value) }
                  })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                  min="0"
                  max="100"
                />
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:px-6 flex justify-between items-center border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900">College Profile</h2>
            {!editing ? (
              <button
                onClick={() => setEditing(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit Profile
              </button>
            ) : (
              <button
                onClick={() => {
                  setEditing(false);
                  setFormData({ ...college });
                }}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </button>
            )}
          </div>

          {error && (
            <div className="px-4 py-3 bg-red-50 text-red-700 text-sm">
              {error}
            </div>
          )}

          {editing ? (
            <form onSubmit={handleSubmit}>
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
                  {[
                    { key: 'basic', label: 'Basic Info', icon: Building },
                    { key: 'address', label: 'Address', icon: MapPin },
                    { key: 'social', label: 'Social Links', icon: Globe },
                    { key: 'degrees', label: 'Degrees', icon: GraduationCap },
                    { key: 'stats', label: 'Statistics', icon: Users }
                  ].map(({ key, label, icon: Icon }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setActiveTab(key)}
                      className={`
                        group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm
                        ${activeTab === key
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }
                      `}
                    >
                      <Icon className={`
                        h-5 w-5 mr-2
                        ${activeTab === key ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500'}
                      `} />
                      {label}
                    </button>
                  ))}
                </nav>
              </div>

              <div className="p-6">
                {renderTabContent()}
              </div>

              <div className="px-6 py-4 bg-gray-50 flex justify-end">
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300"
                >
                  {saving ? (
                    <>
                      <Loader2 className="animate-spin h-4 w-4 mr-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </form>
          ) : (
            <div className="divide-y divide-gray-200">
              {/* View Mode Content */}
              <div className="p-6 space-y-6">
                <div className="flex items-center space-x-6">
                  {formData.logo && (
                    <img 
                      src={formData.logo} 
                      alt="College Logo" 
                      className="w-32 h-32 object-contain rounded-lg"
                    />
                  )}
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">{college.name}</h3>
                    <p className="text-sm text-gray-500">Code: {college.code}</p>
                  </div>
                </div>

                {college.about && (
                  <div className="mt-6">
                    <h4 className="text-lg font-medium text-gray-900">About</h4>
                    <p className="mt-2 text-gray-600">{college.about}</p>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h4 className="text-lg font-medium text-gray-900">Contact Information</h4>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2 text-gray-600">
                        <Mail className="h-5 w-5 text-gray-400" />
                        <span>{college.contactEmail}</span>
                      </div>
                      {college.contact?.phone && (
                        <div className="flex items-center space-x-2 text-gray-600">
                          <Phone className="h-5 w-5 text-gray-400" />
                          <span>{college.contact.phone}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {college.address && (
                    <div className="space-y-4">
                      <h4 className="text-lg font-medium text-gray-900">Location</h4>
                      <div className="space-y-2 text-gray-600">
                        <p>{college.address.street}</p>
                        <p>
                          {college.address.city}, {college.address.state} {college.address.postalCode}
                        </p>
                        <p>{college.address.country}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Statistics */}
              {college.stats && (
                <div className="p-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">College Statistics</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <div className="flex items-center mb-2">
                        <Users className="h-5 w-5 text-blue-500 mr-2" />
                        <span className="text-sm font-medium text-blue-800">Students</span>
                      </div>
                      <p className="text-2xl font-bold text-blue-900">
                        {college.stats.totalStudents?.toLocaleString()}
                      </p>
                    </div>
                    
                    <div className="p-4 bg-green-50 rounded-lg">
                      <div className="flex items-center mb-2">
                        <School className="h-5 w-5 text-green-500 mr-2" />
                        <span className="text-sm font-medium text-green-800">Faculty</span>
                      </div>
                      <p className="text-2xl font-bold text-green-900">
                        {college.stats.totalFaculty?.toLocaleString()}
                      </p>
                    </div>

                    <div className="p-4 bg-purple-50 rounded-lg">
                      <div className="flex items-center mb-2">
                        <Users className="h-5 w-5 text-purple-500 mr-2" />
                        <span className="text-sm font-medium text-purple-800">Alumni</span>
                      </div>
                      <p className="text-2xl font-bold text-purple-900">
                        {college.stats.totalAlumni?.toLocaleString()}
                      </p>
                    </div>

                    <div className="p-4 bg-yellow-50 rounded-lg">
                      <div className="flex items-center mb-2">
                        <Percent className="h-5 w-5 text-yellow-500 mr-2" />
                        <span className="text-sm font-medium text-yellow-800">Placement Rate</span>
                      </div>
                      <p className="text-2xl font-bold text-yellow-900">
                        {college.stats.placementRate}%
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Degrees & Departments */}
{college.degrees && college.degrees.length > 0 && (
  <div className="p-6">
    <h4 className="text-lg font-medium text-gray-900 mb-4">Degrees & Departments</h4>
    <div className="space-y-6">
      {college.degrees.map((degree, index) => (
        <div key={index} className="bg-gray-50 rounded-lg p-4">
          <h5 className="text-md font-medium text-gray-900 mb-3">{degree.name}</h5>
          {degree.departments.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {degree.departments.map((dept, deptIndex) => (
                <div key={deptIndex} className="bg-white rounded p-2 text-sm text-gray-700">
                  {dept.name}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No departments added</p>
          )}
        </div>
      ))}
    </div>
  </div>
)}

              {/* Social Links */}
              {college.socialLinks && Object.values(college.socialLinks).some(link => link) && (
                <div className="p-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Social Media</h4>
                  <div className="flex space-x-4">
                    {college.socialLinks.facebook && (
                      <a href={college.socialLinks.facebook} target="_blank" rel="noopener noreferrer"
                         className="text-gray-400 hover:text-blue-600">
                        <Facebook className="h-6 w-6" />
                      </a>
                    )}
                    {college.socialLinks.twitter && (
                      <a href={college.socialLinks.twitter} target="_blank" rel="noopener noreferrer"
                         className="text-gray-400 hover:text-blue-400">
                        <Twitter className="h-6 w-6" />
                      </a>
                    )}
                    {college.socialLinks.linkedin && (
                      <a href={college.socialLinks.linkedin} target="_blank" rel="noopener noreferrer"
                         className="text-gray-400 hover:text-blue-700">
                        <Linkedin className="h-6 w-6" />
                      </a>
                    )}
                    {college.socialLinks.instagram && (
                      <a href={college.socialLinks.instagram} target="_blank" rel="noopener noreferrer"
                         className="text-gray-400 hover:text-pink-600">
                        <Instagram className="h-6 w-6" />
                      </a>
                    )}
                    {college.socialLinks.youtube && (
                      <a href={college.socialLinks.youtube} target="_blank" rel="noopener noreferrer"
                         className="text-gray-400 hover:text-red-600">
                        <Youtube className="h-6 w-6" />
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CollegeProfile;